import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Invoice, InvoiceItem, Quotation, QuotationItem, Profile } from '../types/database';

interface CompanyInfo {
  name: string;
  tagline: string;
  address: string;
  cityCountry: string;
  phone: string;
  email: string;
  website: string;
}

const BIENTO_INFO: CompanyInfo = {
  name: 'BIENTOMART DIGITAL SOLUTIONS',
  tagline: 'Business Platform & Online Academy',
  address: 'Plot 12 Kampala Road, Asante Media Agency',
  cityCountry: 'Kampala, Uganda',
  phone: '+256 773 515 447',
  email: 'bientomart@gmail.com',
  website: 'www.bientomart.com',
};

/**
 * Generates and downloads a branded PDF invoice for BientoMart
 */
export function generateInvoicePDF(
  invoice: Invoice,
  items: InvoiceItem[] = [],
  clientProfile?: Partial<Profile> | null
) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const primaryColor: [number, number, number] = [11, 26, 58]; // #0B1A3A
  const accentColor: [number, number, number] = [45, 115, 237]; // #2D73ED
  const grayMuted: [number, number, number] = [120, 130, 140];

  // Header Banner Background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 42, 'F');

  // Decorative Accent line
  doc.setFillColor(...accentColor);
  doc.rect(0, 42, pageWidth, 2.5, 'F');

  // Company Brand Name & Tagline
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('BIENTOMART', 14, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(184, 208, 248);
  doc.text(BIENTO_INFO.tagline, 14, 25);
  doc.text(`${BIENTO_INFO.phone}  |  ${BIENTO_INFO.email}  |  ${BIENTO_INFO.website}`, 14, 32);

  // Document Title & Number
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('INVOICE', pageWidth - 14, 20, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(220, 230, 255);
  doc.text(`#${invoice.invoice_number}`, pageWidth - 14, 28, { align: 'right' });

  // Status Badge
  const statusColor: [number, number, number] =
    invoice.status === 'Paid'
      ? [16, 185, 129] // Emerald green
      : invoice.status === 'Partially Paid'
      ? [245, 158, 11] // Amber
      : [239, 68, 68]; // Red

  doc.setFillColor(...statusColor);
  doc.roundedRect(pageWidth - 42, 32, 28, 6, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(invoice.status.toUpperCase(), pageWidth - 28, 36.3, { align: 'center' });

  // Invoice Details & Client Information (Grid)
  const metaY = 54;
  doc.setFontSize(9);

  // Billed To Column
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('BILLED TO:', 14, metaY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const clientName = clientProfile?.full_name || invoice.client?.full_name || 'Valued Client';
  doc.text(clientName, 14, metaY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayMuted);
  const clientEmail = clientProfile?.email || invoice.client?.email || '';
  const clientOrg = clientProfile?.organization_name || invoice.client?.organization_name || '';
  const clientAddress = [clientProfile?.city, clientProfile?.country].filter(Boolean).join(', ');

  let offset = metaY + 11;
  if (clientOrg) {
    doc.text(clientOrg, 14, offset);
    offset += 5;
  }
  if (clientEmail) {
    doc.text(clientEmail, 14, offset);
    offset += 5;
  }
  if (clientAddress) {
    doc.text(clientAddress, 14, offset);
    offset += 5;
  }

  // Invoice Meta Column (Right aligned)
  const metaX = pageWidth - 14;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('INVOICE DETAILS:', metaX, metaY, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayMuted);

  const issuedStr = new Date(invoice.issued_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const dueStr = new Date(invoice.due_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  doc.text(`Issue Date: ${issuedStr}`, metaX, metaY + 6, { align: 'right' });
  doc.text(`Due Date: ${dueStr}`, metaX, metaY + 11, { align: 'right' });
  doc.text(`Currency: ${invoice.currency || 'USD'}`, metaX, metaY + 16, { align: 'right' });

  // Line Items Table
  const tableData = (items.length > 0 ? items : (invoice.items || [])).map((item, idx) => [
    (idx + 1).toString(),
    item.description,
    Number(item.quantity).toFixed(0),
    `$${Number(item.unit_price).toFixed(2)}`,
    `$${Number(item.total_price).toFixed(2)}`,
  ]);

  // Fallback if no items array exists
  if (tableData.length === 0) {
    tableData.push([
      '1',
      invoice.notes || 'Professional Digital Services',
      '1',
      `$${Number(invoice.subtotal || invoice.total_amount).toFixed(2)}`,
      `$${Number(invoice.subtotal || invoice.total_amount).toFixed(2)}`,
    ]);
  }

  const tableStartY = Math.max(offset + 6, metaY + 24);

  autoTable(doc, {
    startY: tableStartY,
    head: [['#', 'Description of Services', 'Qty', 'Unit Price', 'Amount']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto', halign: 'left' },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 32, halign: 'right' },
    },
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 4,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  // Calculate totals position
  // @ts-expect-error jspdf-autotable attaches lastAutoTable to doc
  const finalY = doc.lastAutoTable?.finalY || tableStartY + 40;

  // Financial Totals Box
  const totalsX = pageWidth - 80;
  const totalsY = finalY + 8;

  doc.setFontSize(9);
  doc.setTextColor(...grayMuted);

  // Subtotal
  doc.text('Subtotal:', totalsX, totalsY);
  doc.text(`$${Number(invoice.subtotal || invoice.total_amount).toFixed(2)}`, pageWidth - 14, totalsY, {
    align: 'right',
  });

  // Tax (if applicable)
  let curY = totalsY + 6;
  if (invoice.tax && Number(invoice.tax) > 0) {
    doc.text('Taxes & Duties:', totalsX, curY);
    doc.text(`$${Number(invoice.tax).toFixed(2)}`, pageWidth - 14, curY, { align: 'right' });
    curY += 6;
  }

  // Discount (if applicable)
  if (invoice.discount && Number(invoice.discount) > 0) {
    doc.text('Discount Applied:', totalsX, curY);
    doc.text(`-$${Number(invoice.discount).toFixed(2)}`, pageWidth - 14, curY, { align: 'right' });
    curY += 6;
  }

  // Total Divider line
  doc.setDrawColor(220, 225, 230);
  doc.line(totalsX, curY, pageWidth - 14, curY);
  curY += 6;

  // Total Amount
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.setFontSize(11);
  doc.text('Total Amount:', totalsX, curY);
  doc.text(`$${Number(invoice.total_amount).toFixed(2)} ${invoice.currency || 'USD'}`, pageWidth - 14, curY, {
    align: 'right',
  });
  curY += 7;

  // Amount Paid
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(16, 185, 129); // green
  doc.text('Amount Paid:', totalsX, curY);
  doc.text(`$${Number(invoice.amount_paid || 0).toFixed(2)}`, pageWidth - 14, curY, { align: 'right' });
  curY += 6;

  // Balance Due (Highlight)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const balDue = Number(invoice.balance_due ?? (invoice.total_amount - (invoice.amount_paid || 0)));
  doc.setTextColor(balDue > 0 ? 220 : 16, balDue > 0 ? 38 : 185, balDue > 0 ? 38 : 129);
  doc.text('Balance Due:', totalsX, curY);
  doc.text(`$${balDue.toFixed(2)} ${invoice.currency || 'USD'}`, pageWidth - 14, curY, { align: 'right' });

  // Payment Instructions & Bank Details Box (Left of totals)
  const payBoxWidth = totalsX - 22;
  const payBoxY = finalY + 8;

  doc.setFillColor(245, 248, 255);
  doc.setDrawColor(184, 208, 248);
  doc.roundedRect(14, payBoxY, payBoxWidth, 44, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...accentColor);
  doc.text('OFFICIAL PAYMENT CHANNELS', 18, payBoxY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...primaryColor);
  doc.text('1. Mobile Money (MTN / Airtel Uganda):', 18, payBoxY + 12);
  doc.text('   Recipient: Masengesho Bienvenu | Merchant / Mobile: +256 773 515 447', 18, payBoxY + 16);

  doc.text('2. Bank Wire / Transfer (Stanbic Bank / Centenary):', 18, payBoxY + 22);
  doc.text('   Available upon request - Quote Invoice Number as reference.', 18, payBoxY + 26);

  doc.text('3. Online Credit / Debit Card & Flutterwave:', 18, payBoxY + 32);
  doc.text('   Pay securely online via your client portal at www.bientomart.com/portal', 18, payBoxY + 36);

  // Notes & Terms
  const termsY = Math.max(curY + 14, payBoxY + 52);
  if (invoice.terms || invoice.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...primaryColor);
    doc.text('TERMS & NOTES:', 14, termsY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...grayMuted);
    const noteText = invoice.terms || invoice.notes || '';
    const splitNotes = doc.splitTextToSize(noteText, pageWidth - 28);
    doc.text(splitNotes, 14, termsY + 4);
  }

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 12;
  doc.setDrawColor(230, 235, 240);
  doc.line(14, footerY - 4, pageWidth - 14, footerY - 4);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.setTextColor(...grayMuted);
  doc.text('Thank you for trusting BientoMart Digital Solutions with your business growth.', 14, footerY);
  doc.text(`Generated on ${new Date().toLocaleDateString()} | bientomart.com`, pageWidth - 14, footerY, {
    align: 'right',
  });

  // Save/Download PDF
  doc.save(`BientoMart-Invoice-${invoice.invoice_number}.pdf`);
}

/**
 * Generates and downloads a branded PDF quotation for BientoMart
 */
export function generateQuotationPDF(
  quote: Quotation,
  items: QuotationItem[] = [],
  clientProfile?: Partial<Profile> | null
) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const primaryColor: [number, number, number] = [11, 26, 58]; // #0B1A3A
  const accentColor: [number, number, number] = [45, 115, 237]; // #2D73ED
  const grayMuted: [number, number, number] = [120, 130, 140];

  // Header Banner Background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 42, 'F');

  // Decorative Accent line
  doc.setFillColor(...accentColor);
  doc.rect(0, 42, pageWidth, 2.5, 'F');

  // Company Brand Name & Tagline
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('BIENTOMART', 14, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(184, 208, 248);
  doc.text(BIENTO_INFO.tagline, 14, 25);
  doc.text(`${BIENTO_INFO.phone}  |  ${BIENTO_INFO.email}  |  ${BIENTO_INFO.website}`, 14, 32);

  // Document Title & Number
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('QUOTATION', pageWidth - 14, 20, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(220, 230, 255);
  doc.text(`#${quote.quotation_number}`, pageWidth - 14, 28, { align: 'right' });

  // Status Badge
  const statusColor: [number, number, number] =
    quote.status === 'Accepted'
      ? [16, 185, 129] // Emerald green
      : quote.status === 'Rejected'
      ? [239, 68, 68]
      : [45, 115, 237]; // Blue

  doc.setFillColor(...statusColor);
  doc.roundedRect(pageWidth - 42, 32, 28, 6, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(quote.status.toUpperCase(), pageWidth - 28, 36.3, { align: 'center' });

  // Quote Details & Client Information (Grid)
  const metaY = 54;
  doc.setFontSize(9);

  // Prepared For Column
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('PREPARED FOR:', 14, metaY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const clientName = quote.client_name || clientProfile?.full_name || 'Prospective Partner';
  doc.text(clientName, 14, metaY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayMuted);
  const clientEmail = quote.client_email || clientProfile?.email || '';
  const clientOrg = quote.client_organization || clientProfile?.organization_name || '';

  let offset = metaY + 11;
  if (clientOrg) {
    doc.text(clientOrg, 14, offset);
    offset += 5;
  }
  if (clientEmail) {
    doc.text(clientEmail, 14, offset);
    offset += 5;
  }

  // Quote Meta Column (Right aligned)
  const metaX = pageWidth - 14;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('PROPOSAL DETAILS:', metaX, metaY, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayMuted);

  const issuedStr = new Date(quote.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const validUntilStr = quote.valid_until
    ? new Date(quote.valid_until).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '30 Days from Issuance';

  doc.text(`Proposal Date: ${issuedStr}`, metaX, metaY + 6, { align: 'right' });
  doc.text(`Valid Until: ${validUntilStr}`, metaX, metaY + 11, { align: 'right' });
  doc.text(`Currency: ${quote.currency || 'USD'}`, metaX, metaY + 16, { align: 'right' });

  // Line Items Table
  const tableData = (items.length > 0 ? items : (quote.items || [])).map((item, idx) => [
    (idx + 1).toString(),
    item.description,
    Number(item.quantity).toFixed(0),
    `$${Number(item.unit_price).toFixed(2)}`,
    `$${Number(item.total_price).toFixed(2)}`,
  ]);

  if (tableData.length === 0) {
    tableData.push([
      '1',
      quote.notes || 'Full Scope Digital Delivery',
      '1',
      `$${Number(quote.total_amount).toFixed(2)}`,
      `$${Number(quote.total_amount).toFixed(2)}`,
    ]);
  }

  const tableStartY = Math.max(offset + 6, metaY + 24);

  autoTable(doc, {
    startY: tableStartY,
    head: [['#', 'Proposed Service / Deliverable', 'Qty', 'Unit Rate', 'Amount']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto', halign: 'left' },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 32, halign: 'right' },
    },
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 4,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  // Calculate totals position
  // @ts-expect-error jspdf-autotable attaches lastAutoTable to doc
  const finalY = doc.lastAutoTable?.finalY || tableStartY + 40;

  // Financial Totals Box
  const totalsX = pageWidth - 80;
  const totalsY = finalY + 8;

  doc.setFontSize(9);
  doc.setTextColor(...grayMuted);

  // Subtotal
  doc.text('Subtotal:', totalsX, totalsY);
  doc.text(`$${Number(quote.subtotal || quote.total_amount).toFixed(2)}`, pageWidth - 14, totalsY, {
    align: 'right',
  });

  let curY = totalsY + 6;
  if (quote.discount && Number(quote.discount) > 0) {
    doc.text('Discount Offer:', totalsX, curY);
    doc.text(`-$${Number(quote.discount).toFixed(2)}`, pageWidth - 14, curY, { align: 'right' });
    curY += 6;
  }

  if (quote.tax && Number(quote.tax) > 0) {
    doc.text('Estimated Tax:', totalsX, curY);
    doc.text(`$${Number(quote.tax).toFixed(2)}`, pageWidth - 14, curY, { align: 'right' });
    curY += 6;
  }

  // Divider line
  doc.setDrawColor(220, 225, 230);
  doc.line(totalsX, curY, pageWidth - 14, curY);
  curY += 6;

  // Total Quotation
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.setFontSize(11);
  doc.text('Total Quotation:', totalsX, curY);
  doc.text(`$${Number(quote.total_amount).toFixed(2)} ${quote.currency || 'USD'}`, pageWidth - 14, curY, {
    align: 'right',
  });

  // Terms and acceptance
  const termsY = curY + 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...primaryColor);
  doc.text('TERMS & SCOPE ACCEPTANCE:', 14, termsY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...grayMuted);
  const termsContent =
    quote.terms ||
    '1. 50% deposit required upon quotation acceptance to initiate milestones.\n' +
      '2. Revisions included within 14 days of prototype presentation.\n' +
      '3. To accept this proposal, click "Accept Quotation" in your client portal or reply in writing.';

  const splitTerms = doc.splitTextToSize(termsContent, pageWidth - 28);
  doc.text(splitTerms, 14, termsY + 5);

  // Signature Block
  const sigY = termsY + 28;
  doc.setDrawColor(180, 190, 200);
  doc.line(14, sigY, 70, sigY);
  doc.line(pageWidth - 70, sigY, pageWidth - 14, sigY);

  doc.setFontSize(7.5);
  doc.text('Authorized Signature (BientoMart)', 14, sigY + 4);
  doc.text('Client Acceptance Signature', pageWidth - 70, sigY + 4);

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 12;
  doc.setDrawColor(230, 235, 240);
  doc.line(14, footerY - 4, pageWidth - 14, footerY - 4);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.setTextColor(...grayMuted);
  doc.text('BientoMart Digital Solutions — Empowering East African Businesses.', 14, footerY);
  doc.text(`Quotation valid through ${validUntilStr}`, pageWidth - 14, footerY, { align: 'right' });

  // Save/Download PDF
  doc.save(`BientoMart-Quotation-${quote.quotation_number}.pdf`);
}
